/* eslint-disable @typescript-eslint/no-explicit-any */
import { Content, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { useEffect, useRef } from 'react';

import Icon from 'molecules/Icon';
import Link from 'molecules/Link';
import useHeaderStore from 'molecules/TableOfContents/store';
import useTocStore, { setActiveItem } from 'molecules/TableOfContents/store';

import { TocItemField } from 'layout/article/config';

import { hasArrayValues } from 'utils/array/arrays';
import { toKebabCase } from 'utils/strings';

import type { FC } from 'react';

type ActivePath = string;
type ToCItems = { items: TocItemField[]; activePath: ActivePath };
type ScrollItemsProps = { items?: TocItemField[] };

interface TableOfContentsProps {
  label?: string;
  items?: TocItemField[];
  activePath?: Maybe<ActivePath>;
}

// FIXED: Both hover and active states use bg-primary-1000 (#22828A) with white text
const ToCLinkStyles =
  'flex w-full rounded-lg p-3 cursor-pointer text-gray-900 transition-colors hover:bg-primary-1000 hover:text-white focus:border focus:border-solid focus:border-primary-700 data-[active=true]:bg-primary-1000 data-[active=true]:text-white data-[active=true]:shadow-tocTabBoxShadow';

const isMapLike = (v: unknown) => !!v && typeof v === 'object' && typeof (v as any).entries === 'function';

const toPlain = (v: any) => (isMapLike(v) ? Object.fromEntries((v as Map<string, unknown>).entries()) : v);

const normalizeItems = (raw?: TocItemField[] | any): TocItemField[] => {
  if (!raw) {
    return [];
  }

  const arr = Array.isArray(raw) ? raw : [raw];

  return arr
    .map((x, i) => {
      const o: any = toPlain(x) ?? {};

      const id = o.id ?? `contents-${i}`;
      const label = o.label ?? 'Heading';
      const link = o.link ?? '#heading';
      const scrollLink = o.scrollLink ?? undefined;

      return {
        id,
        label,
        link,
        scrollLink,
      } as TocItemField;
    })
    .filter(i => !!i.label && i.label.trim().length > 0);
};

const shouldUseLinkMode = (items: TocItemField[], activePath?: string | null) => {
  if (!hasArrayValues(items)) {
    return false;
  }

  if (items.some(i => i.scrollLink)) {
    return false;
  }

  const hasValidNavigationLinks = items.every(i => {
    if (typeof i.link !== 'string' || i.link.length <= 1) {
      return false;
    }

    if (i.link === '#heading' || i.link === '#') {
      return false;
    }

    const isRoute = i.link.startsWith('/') || i.link.startsWith('http');
    const isDifferentPage = activePath ? i.link !== activePath : true;

    return isRoute && isDifferentPage;
  });

  return hasValidNavigationLinks;
};

const ScrollItems: FC<ScrollItemsProps> = ({ items }) => {
  const activeItem = useHeaderStore(state => state.activeItem);
  const generatedHeadings = useTocStore(state => state.headings);
  const base = hasArrayValues(items) ? items! : generatedHeadings;
  const headings = normalizeItems(base);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    setActiveItem(0);
  }, []);

  const scrollToTarget = (target: string, itemLabel: string, index: number) => {
    if (isScrollingRef.current) {
      return;
    }

    let element: HTMLElement | null = document.getElementById(target);

    if (!element) {
      const alternatives = [
        target.replace(/-/g, ''),
        target.replace(/-da-/g, '-'),
        target.replace(/-([a-z])/g, (_m, letter) => letter.toUpperCase()),
      ];

      for (const alt of alternatives) {
        element = document.getElementById(alt);
        if (element) {
          break;
        }
      }
    }

    if (!element) {
      const allHeadings = Array.from(document.querySelectorAll('h1, h2, h3, h4'));
      const foundHeading = allHeadings.find(h => h.textContent?.trim() === itemLabel);

      if (foundHeading) {
        element = foundHeading as HTMLElement;
      }
    }

    if (!element) {
      console.error(`❌ Could not find element for: "${itemLabel}" (target: "${target}")`);

      return;
    }

    isScrollingRef.current = true;

    const yOffset = -80;
    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 1800;
    let startTime: number | null = null;
    let hasSetActive = false;

    const easeInOutQuart = (t: number): number => (t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2);

    const animation = (currentTime: number) => {
      if (startTime === null) {
        startTime = currentTime;
      }
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = easeInOutQuart(progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (!hasSetActive && progress > 0.1) {
        setActiveItem(index);
        hasSetActive = true;
      }

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      } else {
        isScrollingRef.current = false;
      }
    };

    requestAnimationFrame(animation);
  };

  return (
    <>
      {headings.map((item, index) => {
        const target = item.scrollLink ? toKebabCase(item.scrollLink) : toKebabCase(item.label || '');
        const key = item.id || target || `toc-item-${index}`;
        const itemLabel = item.label || 'Section';

        return (
          <li
            key={key}
            className={ToCLinkStyles}
            data-active={index === activeItem}
            aria-label="Table of Contents Link"
          >
            <a
              href={`#${target}`}
              className="w-full text-md font-medium"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                scrollToTarget(target, itemLabel, index);
              }}
            >
              {itemLabel}
            </a>
          </li>
        );
      })}
    </>
  );
};

const normalizePath = (path: string): string => path.replace(/\/+$/, '') || '/';

const LinkItems: FC<ToCItems> = ({ items, activePath }) => {
  const normalized = normalizeItems(items);
  const normalizedActivePath = normalizePath(activePath);

  return (
    <>
      {normalized.map((item, index) => {
        const key = item.id || toKebabCase(item.label || '') || `toc-link-${index}`;
        const isActive = item.link ? normalizePath(item.link) === normalizedActivePath : false;

        return (
          <li key={key} className={ToCLinkStyles} data-active={isActive} aria-label="Table of Contents Link">
            <Link
              href={item.link}
              className="w-full text-md font-medium"
              aria-label={`Link to ${item.label}`}
              enablePreview={false}
            >
              {item.label}
            </Link>
          </li>
        );
      })}
    </>
  );
};

const TableOfContents: FC<TableOfContentsProps> = ({ label, items, activePath }) => {
  const normalized = normalizeItems(items);
  const linkMode = shouldUseLinkMode(normalized, activePath);

  return (
    <Root type="single" defaultValue="toc" collapsible className="flex w-full flex-col gap-4">
      <Item value="toc">
        <Trigger className="group flex w-full flex-row items-center justify-between gap-4 border-b border-b-gray-400 pb-4 text-gray-900 dark:text-white">
          <span className="text-left font-spaceGrotesk text-xl font-bold lg:block">{label || 'In this article'}</span>
          <Icon
            icon="chevron-down"
            size={24}
            className="stroke-2 transition-transform group-data-[state=open]:rotate-180"
          />
        </Trigger>
        <Content className="w-full overflow-hidden data-[state=closed]:animate-accordionSlideUp">
          <ul className="flex w-full flex-col gap-2 pt-4">
            {linkMode ? (
              <LinkItems items={normalized} activePath={activePath || ''} />
            ) : (
              <ScrollItems items={normalized} />
            )}
          </ul>
        </Content>
      </Item>
    </Root>
  );
};

export default TableOfContents;
